import type { ApplicantSectionV2 } from "@credit-acceptance/step6-bff-client";
import { toast } from "@credit-acceptance/uikit";
import { Loader2, Zap } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useReadOnly } from "../lock/ReadOnlyContext";
import ViewBureauModal from "../viewBureau/ViewBureauModal";
import { isRunnableActionId } from "./actionRegistry";
import { downloadDealSummaryPdf } from "./api/downloadDealSummaryPdf";
import { openMailContract } from "./api/openMailContract";
import { printCacVscGapForms } from "./api/printCacVscGapForms";
import { printFundingFax } from "./api/printFundingFax";
import { fundingFaxResult, sendFundingFaxErrorKey, useSendFundingFax } from "./api/useSendFundingFax";
import type { WorkbenchAction } from "./api/useWorkbenchActions";
import FabPopover from "./FabPopover";

export const ACTIONS_PANEL_ID = "actions-panel";

type ActionsPopoverProperties = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  approvalId: string;
  /** Driver applicant section from the dashboard response, forwarded to the View Bureau modal. */
  driver: ApplicantSectionV2 | undefined;
  /** 2nd signer applicant section, forwarded to the View Bureau modal. */
  secondSigner: ApplicantSectionV2 | undefined;
  /** Dealer id forwarded to the View Bureau modal's repull call. */
  dealerId: string | undefined;
  /** Role-scoped actions for the popover; those without a handler render disabled. */
  actions: WorkbenchAction[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
};

function ActionsPopover({
  open,
  onOpenChange,
  approvalId,
  driver,
  secondSigner,
  dealerId,
  actions,
  isLoading,
  isError,
  onRetry,
}: ActionsPopoverProperties) {
  const { t } = useTranslation();
  const [viewBureauOpen, setViewBureauOpen] = useState(false);
  const readOnly = useReadOnly();
  const sendFundingFax = useSendFundingFax(approvalId);
  // Id of the action currently running, or undefined when idle. Sending the
  // funding fax is not idempotent, so while one is in flight every menu item is
  // disabled and the running one renders a spinner — the popover deliberately
  // stays open so the analyst can see the request is still working.
  const [runningActionId, setRunningActionId] = useState<string | undefined>();
  // `runningActionId` is React state, so it isn't visible to a second click
  // dispatched before the re-render lands. The ref closes that window: without
  // it a double-click could fax the dealer twice. Mirrors handleConfirm in
  // ExitAccountModal.
  const isRunningReference = useRef(false);
  // Lets the settle handler below see whether the popover is *still* open
  // without re-creating the callback on every open/close.
  const openReference = useRef(open);
  openReference.current = open;

  const runSendFundingFax = async () => {
    isRunningReference.current = true;
    setRunningActionId("send-funding-fax");
    try {
      const { sent, key } = fundingFaxResult(await sendFundingFax.mutateAsync());
      if (sent) {
        toast.success(t(key));
      } else {
        toast.error(t(key));
      }
    } catch (error) {
      toast.error(t(sendFundingFaxErrorKey(error)));
    } finally {
      isRunningReference.current = false;
      setRunningActionId(undefined);
      // Only dismiss the popover if it's still the open panel. The analyst can
      // close it mid-flight (Escape / outside click) and open the Internal /
      // Audit drawer instead; closing unconditionally would shut that drawer,
      // because the cluster tracks a single shared open panel.
      if (openReference.current) {
        onOpenChange(false);
      }
    }
  };

  const handleActionClick = async (actionId: string) => {
    if (readOnly || isLoading || isError || !actions.some((action) => action.id === actionId)) {
      toast.error(t("workbench.actionNotAllowed"));
      return;
    }
    if (isRunningReference.current) {
      return;
    }

    if (actionId === "send-funding-fax") {
      await runSendFundingFax();
      return;
    }

    onOpenChange(false);

    if (!isRunnableActionId(actionId)) {
      return;
    }

    switch (actionId) {
      case "view-bureau": {
        setViewBureauOpen(true);
        break;
      }
      case "print-step5-deal-summary": {
        downloadDealSummaryPdf(approvalId);
        break;
      }
      case "print-funding-fax": {
        printFundingFax(approvalId);
        break;
      }
      case "print-cac-vsc-gap-forms": {
        printCacVscGapForms(approvalId);
        break;
      }
      case "mail-contract": {
        try {
          await openMailContract(approvalId);
        } catch {
          toast.error(t("actions.mailContractError"));
        }
        break;
      }
    }
  };

  return (
    <>
      <FabPopover
        label={t("actions.menu")}
        icon={<Zap className="size-6" />}
        variant="navy"
        panelId={ACTIONS_PANEL_ID}
        open={open}
        onOpenChange={onOpenChange}
        disabled={readOnly}
        contentClassName="w-56 p-1"
      >
        {isLoading ? (
          <p role="status" className="text-muted-foreground px-3 py-2 text-sm">
            {t("actions.loading")}
          </p>
        ) : isError ? (
          <div role="alert" className="flex flex-col gap-2 px-3 py-2 text-sm">
            <span>{t("actions.loadError")}</span>
            <button type="button" className="text-left font-medium underline" onClick={onRetry}>
              {t("actions.retry")}
            </button>
          </div>
        ) : actions.length === 0 ? (
          <p className="text-muted-foreground px-3 py-2 text-sm">{t("actions.empty")}</p>
        ) : (
          <ul role="menu" aria-label={t("actions.menu")}>
            {actions.map((item) => {
              const isRunning = runningActionId === item.id;
              const isBusy = runningActionId !== undefined;
              return (
                <li key={item.id} role="none">
                  <button
                    type="button"
                    role="menuitem"
                    disabled={readOnly || !isRunnableActionId(item.id) || isBusy}
                    aria-busy={isRunning || undefined}
                    title={isRunnableActionId(item.id) ? undefined : t("actions.comingSoon")}
                    className="hover:bg-accent disabled:text-muted-foreground focus-visible:bg-accent text-foreground flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm outline-none transition-colors disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    onClick={() => void handleActionClick(item.id)}
                  >
                    {isRunning && <Loader2 aria-hidden="true" className="size-4 shrink-0 animate-spin" />}
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </FabPopover>
      <ViewBureauModal
        open={viewBureauOpen && actions.some((action) => action.id === "view-bureau")}
        onOpenChange={setViewBureauOpen}
        approvalId={approvalId}
        driver={driver}
        secondSigner={secondSigner}
        dealerId={dealerId}
        canRepull={!readOnly && !isLoading && !isError}
      />
    </>
  );
}

export default ActionsPopover;
